import React, { useEffect, useState } from 'react';
import {
  Box, Button, Alert, Typography, Paper, Grid, Divider, MenuItem, TextField,
  FormControlLabel, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate, useParams } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { apiErrorMessage, statusChip } from '../../components/superadmin/SuperAdminDataTable';
import { PlatformBillingInvoicePrint, printPlatformInvoice, downloadPlatformInvoicePdf } from '../../components/superadmin/PlatformBillingInvoicePrint';
import { useLocale } from '../../contexts/LocaleContext';
import { useInvoiceLocale } from '../../hooks/useInvoiceLocale';
import { RADIUS, brandColors } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

const PAYMENT_MODES = [
  { value: 'pending', label: 'Pending' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'razorpay', label: 'Razorpay' },
  { value: 'other', label: 'Other' },
];

export const BillingInvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [edit, setEdit] = useState({
    baseAmount: 0,
    gstEnabled: true,
    gstRate: 18,
    discountAmount: 0,
    discountPercent: 0,
    paymentMode: 'pending',
    status: 'pending',
    billingPeriod: '',
    dueDate: '',
    planName: '',
    itemDescription: '',
    hsnSac: '998314',
    billingCycle: 'monthly',
    localeLanguage: 'en',
    localeCurrency: 'INR',
    localeTimezone: 'Asia/Kolkata',
    localeDateFormat: 'DD/MM/YYYY',
  });
  const { languages, currencies, timezones, dateFormats } = useLocale();
  const invoiceLocale = useInvoiceLocale(data?.invoice);
  const { formatCurrency, formatDate, t } = invoiceLocale;
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const res = await superAdminAPI.getBillingInvoice(id);
      setData(res.data);
      const inv = res.data.invoice;
      setEmailTo(inv.tenantEmail || res.data.company?.companyEmail || '');
      setEdit({
        baseAmount: inv.baseAmount,
        gstEnabled: inv.gstEnabled,
        gstRate: inv.gstRate,
        discountAmount: inv.discountAmount || 0,
        discountPercent: inv.discountPercent || 0,
        paymentMode: inv.paymentMode || 'pending',
        status: inv.status,
        billingPeriod: inv.billingPeriod || '',
        dueDate: inv.dueDate ? String(inv.dueDate).slice(0, 10) : '',
        planName: inv.planName || '',
        itemDescription: inv.itemDescription || inv.planName || '',
        hsnSac: inv.hsnSac || res.data.plan?.hsnSac || '998314',
        billingCycle: inv.billingCycle || 'monthly',
        localeLanguage: inv.localeLanguage || 'en',
        localeCurrency: inv.localeCurrency || 'INR',
        localeTimezone: inv.localeTimezone || 'Asia/Kolkata',
        localeDateFormat: inv.localeDateFormat || 'DD/MM/YYYY',
      });
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load invoice'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleSaveBilling = async () => {
    if (!id) return;
    try {
      await superAdminAPI.updateBillingInvoice(id, edit);
      setMessage('Invoice billing details updated');
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Update failed'));
    }
  };

  const handleStatusUpdate = async () => {
    if (!id) return;
    try {
      await superAdminAPI.updateBillingInvoiceStatus(id, edit.status, undefined, edit.paymentMode);
      setMessage('Status updated');
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Status update failed'));
    }
  };

  const handleSendEmail = async () => {
    if (!id || !emailTo.trim()) return;
    setEmailSending(true);
    setError('');
    try {
      const res = await superAdminAPI.sendInvoiceEmail(id, { to: emailTo.trim() });
      setMessage(res.data.message || 'Invoice email sent');
      setEmailOpen(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to send email'));
    } finally {
      setEmailSending(false);
    }
  };

  const handleDownloadPdf = async () => {
    const num = data?.invoice?.invoiceNumber;
    if (!num) return;
    setPdfLoading(true);
    try {
      await downloadPlatformInvoicePdf(num);
    } catch {
      setError('PDF download failed. Try Print instead.');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}><Typography>Loading invoice...</Typography></Box>;
  }

  if (!data?.invoice) {
    return <Box sx={{ p: 3 }}><Alert severity="error">Invoice not found</Alert></Box>;
  }

  const invoice = data.invoice;
  const subscription = data.subscription;

  return (
    <Box>
      <PageHeader
        title={invoice.invoiceNumber}
        subtitle={`Platform billing · ${invoice.companyName} · ${invoice.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}`}
        icon={<ReceiptLongIcon />}

        action={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<EmailIcon />} onClick={() => setEmailOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Send Email
            </Button>
            <Button variant="outlined" startIcon={<PictureAsPdfIcon />}
              onClick={handleDownloadPdf} disabled={pdfLoading}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {pdfLoading ? 'Generating…' : t('downloadPdf')}
            </Button>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={printPlatformInvoice} sx={catchyPrimaryButtonSx}>
              {t('printInvoice')}
            </Button>
          </Box>
        }
      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 0, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}`, overflow: 'hidden' }}>
            <PlatformBillingInvoicePrint
              invoice={{ ...invoice, itemDescription: edit.itemDescription, hsnSac: edit.hsnSac, planName: edit.planName }}
              platform={data.platform}
              company={data.company}
              plan={data.plan}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
            <Typography fontWeight={700} mb={2}>Invoice Number</Typography>
            <Typography fontFamily="monospace" fontWeight={800} fontSize={18} color="primary" mb={2}>
              {invoice.invoiceNumber}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Divider sx={{ my: 2 }} />

            <Typography fontWeight={700} mb={2}>Locale</Typography>
            <TextField select fullWidth size="small" label="Language" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.localeLanguage}
              onChange={(e) => setEdit((f) => ({ ...f, localeLanguage: e.target.value }))}>
              {languages.map((l) => <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>)}
            </TextField>
            <TextField select fullWidth size="small" label="Currency" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.localeCurrency}
              onChange={(e) => setEdit((f) => ({ ...f, localeCurrency: e.target.value }))}>
              {currencies.map((c) => <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>)}
            </TextField>
            <TextField select fullWidth size="small" label="Timezone" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.localeTimezone}
              onChange={(e) => setEdit((f) => ({ ...f, localeTimezone: e.target.value }))}>
              {timezones.map((tz) => <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>)}
            </TextField>
            <TextField select fullWidth size="small" label="Date Format" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.localeDateFormat}
              onChange={(e) => setEdit((f) => ({ ...f, localeDateFormat: e.target.value }))}>
              {dateFormats.map((df) => <MenuItem key={df.value} value={df.value}>{df.label}</MenuItem>)}
            </TextField>

            <Divider sx={{ my: 2 }} />

            <Typography fontWeight={700} mb={2}>Plan & Period</Typography>
            <TextField fullWidth size="small" label="Plan Name" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.planName}
              onChange={(e) => setEdit((f) => ({ ...f, planName: e.target.value, itemDescription: f.itemDescription || e.target.value }))}
              helperText="Used on invoice line item" />
            <TextField fullWidth size="small" label="Item & Description (invoice)" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.itemDescription}
              onChange={(e) => setEdit((f) => ({ ...f, itemDescription: e.target.value }))}
              helperText="e.g. CRM Development — shown on printed invoice" />
            <TextField fullWidth size="small" label="HSN / SAC" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.hsnSac}
              onChange={(e) => setEdit((f) => ({ ...f, hsnSac: e.target.value }))} />
            <TextField select fullWidth size="small" label="Billing Cycle" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.billingCycle}
              onChange={(e) => setEdit((f) => ({ ...f, billingCycle: e.target.value }))}>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>
            <TextField fullWidth size="small" label="Billing Period" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.billingPeriod}
              onChange={(e) => setEdit((f) => ({ ...f, billingPeriod: e.target.value }))} />
            <TextField fullWidth type="date" size="small" label="Due Date" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.dueDate}
              onChange={(e) => setEdit((f) => ({ ...f, dueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }} />

            <Typography fontWeight={700} mb={2}>Tax & Discount</Typography>
            <TextField fullWidth type="number" size="small" label="Base Amount" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.baseAmount}
              onChange={(e) => setEdit((f) => ({ ...f, baseAmount: Number(e.target.value) }))} />
            <TextField fullWidth type="number" size="small" label="Discount (₹)" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.discountAmount || ''}
              onChange={(e) => setEdit((f) => ({ ...f, discountAmount: Number(e.target.value), discountPercent: 0 }))} />
            <TextField fullWidth type="number" size="small" label="Discount (%)" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.discountPercent || ''}
              onChange={(e) => setEdit((f) => ({ ...f, discountPercent: Number(e.target.value), discountAmount: 0 }))} />
            <FormControlLabel
              control={<Switch checked={edit.gstEnabled}
                onChange={(e) => setEdit((f) => ({ ...f, gstEnabled: e.target.checked }))} />}
              label="GST / Tax enabled"
            />
            <TextField fullWidth type="number" size="small" label="GST Rate (%)" sx={{ mb: 2, mt: 1, ...catchyTextFieldSx }}
              value={edit.gstRate} disabled={!edit.gstEnabled}
              onChange={(e) => setEdit((f) => ({ ...f, gstRate: Number(e.target.value) }))} />
            <TextField select fullWidth size="small" label="Mode of Payment" sx={{ mb: 2, ...catchyTextFieldSx }}
              value={edit.paymentMode}
              onChange={(e) => setEdit((f) => ({ ...f, paymentMode: e.target.value }))}>
              {PAYMENT_MODES.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>
            <Button fullWidth variant="outlined" startIcon={<SaveIcon />} onClick={handleSaveBilling}>
              Save Billing Details
            </Button>

            <Divider sx={{ my: 2 }} />

            <Typography fontWeight={700} mb={2}>Status</Typography>
            <TextField select fullWidth size="small" value={edit.status}
              onChange={(e) => setEdit((f) => ({ ...f, status: e.target.value }))}
              sx={{ mb: 2, ...catchyTextFieldSx }}>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="partially_paid">Partially Paid</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </TextField>
            <Button fullWidth variant="contained" startIcon={<CheckCircleIcon />}
              onClick={handleStatusUpdate} sx={catchyPrimaryButtonSx}>
              Update Status
            </Button>
            <Button fullWidth sx={{ mt: 1 }} onClick={() => navigate('/super-admin/invoices')}>
              Back to Invoices
            </Button>
          </Paper>

          <Paper sx={{ p: 2, mt: 2, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
            <Typography fontSize={13} fontWeight={700}>Totals (current)</Typography>
            <Typography fontSize={13}>Subtotal: {formatCurrency(invoice.taxableAmount ?? invoice.baseAmount)}</Typography>
            <Typography fontSize={13}>GST: {formatCurrency(invoice.gstAmount)}</Typography>
            <Typography fontSize={15} fontWeight={800}>Total: {formatCurrency(invoice.totalAmount)}</Typography>
          </Paper>

          {subscription && (
            <Paper sx={{ p: 3, mt: 2, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
              <Typography fontWeight={700} mb={1}>Subscription</Typography>
              <Typography fontSize={13}>Cycle: {subscription.billingCycle}</Typography>
              <Typography fontSize={13}>Next billing: {formatDate(subscription.nextBillingDate)}</Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Send Invoice by Email</DialogTitle>
        <DialogContent>
          <Typography fontSize={13} color="text.secondary" mb={2}>
            Email invoice summary to tenant. Configure SMTP in server .env for live delivery.
          </Typography>
          <TextField fullWidth label="Recipient Email" value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)} sx={catchyTextFieldSx} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmailOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendEmail} disabled={emailSending || !emailTo.trim()} sx={catchyPrimaryButtonSx}>
            {emailSending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
